import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { GenerateIntegrationTokenDialog } from '@/features/integrations/components/dialogs/generate-integration-token-dialog';
import { ViewIntegrationTokensDialog } from '@/features/integrations/components/dialogs/view-integration-tokens-dialog';
import { IntegrationsIcon } from '@/features/integrations/components/integrations-icon';
import {
  ACTIVE_PROJECT_INTEGRATION_TOKENS_QUERY_KEY,
  useActiveProjectIntegrationTokens,
} from '@/features/integrations/hooks/use-active-project-token';
import { ACTIVE_INTEGRATIONS_QUERY_KEY } from '@/features/integrations/hooks/use-integration-tokens';
import { useOrgSubscription } from '@/features/subscriptions/hooks/use-org-subscription';
import { Button } from '@/shared/components/ui/button';
import { useDisclosure } from '@/shared/hooks/use-disclosure';

export const CreateIntegrationButton = ({
  projectId,
}: {
  projectId: number;
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('integrations', {
    keyPrefix: 'CreateIntegrationButton',
  });
  const {
    isOpen: isGenerateDialogOpen,
    open: openGenerateDialog,
    close: closeGenerateDialog,
  } = useDisclosure();
  const {
    isOpen: isViewTokensDialogOpen,
    open: openViewTokensDialog,
    close: closeViewTokensDialog,
  } = useDisclosure();
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [generatedTokenName, setGeneratedTokenName] = useState<string>('');

  const { data: activeProjectIntegrationTokens } =
    useActiveProjectIntegrationTokens(Number(projectId));

  const { data: currentOrgPlan } = useOrgSubscription();
  const navigate = useNavigate();

  const hasActiveProjectIntegrationTokens =
    activeProjectIntegrationTokens && activeProjectIntegrationTokens.length > 0;

  const subscriptionGrantsAccessToIntegrationsFeature =
    currentOrgPlan?.subscription?.plan_type === 'enterprise';

  const showIconButton =
    hasActiveProjectIntegrationTokens &&
    subscriptionGrantsAccessToIntegrationsFeature;

  const handleClick = () => {
    if (!subscriptionGrantsAccessToIntegrationsFeature) {
      navigate(`${paths.plansManagement.path}`);
      return;
    }
    if (hasActiveProjectIntegrationTokens) {
      openViewTokensDialog();
    } else {
      openGenerateDialog();
    }
  };

  const handleGenerateSuccess = (token: string, tokenName: string) => {
    setGeneratedToken(token);
    setGeneratedTokenName(tokenName);
    queryClient.invalidateQueries({
      queryKey: [ACTIVE_PROJECT_INTEGRATION_TOKENS_QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [ACTIVE_INTEGRATIONS_QUERY_KEY],
    });
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      closeGenerateDialog();
      setTimeout(() => {
        setGeneratedToken(null);
        setGeneratedTokenName('');
      }, 200);
    }
  };

  return (
    <>
      <Button
        variant={'secondary'}
        btnType={showIconButton ? 'icon' : 'secondary'}
        onClick={handleClick}
      >
        <IntegrationsIcon withMargin={showIconButton} />
        {!showIconButton && <p className="hidden sm:block">{t('label')}</p>}
      </Button>

      <GenerateIntegrationTokenDialog
        open={isGenerateDialogOpen}
        onOpenChange={handleDialogClose}
        projectId={projectId}
        onSuccess={handleGenerateSuccess}
        generatedToken={generatedToken}
        generatedTokenName={generatedTokenName}
      />

      {hasActiveProjectIntegrationTokens && (
        <ViewIntegrationTokensDialog
          open={isViewTokensDialogOpen}
          onOpenChange={closeViewTokensDialog}
          tokens={activeProjectIntegrationTokens || []}
          projectId={projectId}
        />
      )}
    </>
  );
};
